import React from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import {
  DEFAULT_INVOICE_REPORT_OPTIONS,
  getApplicableInvoiceReportSections,
} from "../../../helpers/invoiceReport";
import { resolveInvoiceStatus } from "../../../helpers/invoices";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "20px",
  marginBottom: "16px",
};

const SECTION_TITLE_STYLE = {
  fontWeight: 700,
  fontSize: "15px",
  color: "#c9a84c",
  marginBottom: "10px",
};

const CHECK_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 0",
  borderBottom: "1px solid rgba(201,168,76,0.12)",
  fontSize: "14px",
};

const RADIO_GROUP_STYLE = {
  marginLeft: "30px",
  marginBottom: "8px",
};

const RADIO_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px 0",
  fontSize: "14px",
  cursor: "pointer",
};

function SectionBlock({ title, children }) {
  return (
    <div style={CARD_STYLE}>
      <div style={SECTION_TITLE_STYLE}>{title}</div>
      {children}
    </div>
  );
}

function CheckboxRow({ label, checked, disabled, onChange }) {
  return (
    <label
      style={{
        ...CHECK_ROW_STYLE,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ width: "18px", height: "18px", accentColor: "#c9a84c" }}
      />
      <span>{label}</span>
    </label>
  );
}

function RadioRow({ label, checked, name, disabled = false, onChange }) {
  return (
    <label
      style={{
        ...RADIO_ROW_STYLE,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ accentColor: "#c9a84c" }}
      />
      <span>{label}</span>
    </label>
  );
}

const DATE_INPUT_STYLE = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid rgba(201,168,76,0.25)",
  background: "rgba(0,0,0,0.2)",
  color: "inherit",
  fontSize: "14px",
};

function LineTypeReportOptionsBlock({ section, options, onToggle, onUpdate }) {
  const showSection = options[section.showKey] !== false;
  const detailLevel = options[section.detailLevelKey] || "summary";
  const isDetailed = detailLevel === "detailed";

  return (
    <SectionBlock title={section.title}>
      <CheckboxRow
        label={`Show ${section.title} Section`}
        checked={showSection}
        onChange={() => onToggle(section.showKey)}
      />

      {showSection && (
        <>
          <div style={RADIO_GROUP_STYLE}>
            <RadioRow
              name={section.detailLevelKey}
              label="Summary Only"
              checked={detailLevel === "summary"}
              onChange={() =>
                onUpdate({ [section.detailLevelKey]: "summary" })
              }
            />
            <RadioRow
              name={section.detailLevelKey}
              label="Detailed"
              checked={isDetailed}
              onChange={() =>
                onUpdate({ [section.detailLevelKey]: "detailed" })
              }
            />
          </div>

          {isDetailed &&
            (section.detailOptions || []).map((option) => (
              <CheckboxRow
                key={option.key}
                label={option.label}
                checked={Boolean(options[option.key])}
                onChange={() => onToggle(option.key)}
              />
            ))}
        </>
      )}
    </SectionBlock>
  );
}

export function InvoiceReportOptionsScreen({
  invoiceNumber,
  invoiceTitle,
  invoiceStatus,
  lineItems = [],
  options,
  onChange,
  onPreview,
  onBack,
}) {
  const mergedOptions = { ...DEFAULT_INVOICE_REPORT_OPTIONS, ...options };
  const isDraft = resolveInvoiceStatus(invoiceStatus) === "draft";
  const billingPeriodLocked = !isDraft;
  const customBillingRange = mergedOptions.billingPeriodMode === "custom_range";
  const applicableSections = getApplicableInvoiceReportSections(lineItems);

  function update(patch) {
    onChange?.({ ...mergedOptions, ...patch });
  }

  function toggle(key) {
    update({ [key]: !mergedOptions[key] });
  }

  return (
    <CrewPilotModuleShell
      title="Report Options"
      subtitle={invoiceNumber || invoiceTitle || "Invoice"}
      sectionLabel="Invoice"
      onBack={onBack}
    >
      {applicableSections.length === 0 ? (
        <SectionBlock title="Line Items">
          <p style={{ margin: 0, opacity: 0.7, fontSize: "14px" }}>
            This invoice has no active line items to include in the report.
          </p>
        </SectionBlock>
      ) : (
        applicableSections.map((section) => (
          <LineTypeReportOptionsBlock
            key={section.id}
            section={section}
            options={mergedOptions}
            onToggle={toggle}
            onUpdate={update}
          />
        ))
      )}

      <SectionBlock title="Financial">
        <CheckboxRow
          label="Show Taxes"
          checked={mergedOptions.showTaxes}
          onChange={() => toggle("showTaxes")}
        />
        <CheckboxRow
          label="Show Grand Total"
          checked={mergedOptions.showGrandTotal}
          onChange={() => toggle("showGrandTotal")}
        />
      </SectionBlock>

      <SectionBlock title="Billing Period">
        <div style={RADIO_GROUP_STYLE}>
          <RadioRow
            name="billingPeriodMode"
            label="Entire Project"
            checked={mergedOptions.billingPeriodMode === "entire_project"}
            disabled={billingPeriodLocked}
            onChange={() =>
              update({
                billingPeriodMode: "entire_project",
                billingPeriodStart: "",
                billingPeriodEnd: "",
              })
            }
          />
          <RadioRow
            name="billingPeriodMode"
            label="Custom Date Range"
            checked={customBillingRange}
            disabled={billingPeriodLocked}
            onChange={() =>
              update({
                billingPeriodMode: "custom_range",
                billingPeriodStart:
                  mergedOptions.billingPeriodStart ||
                  new Date().toISOString().slice(0, 10),
                billingPeriodEnd:
                  mergedOptions.billingPeriodEnd ||
                  new Date().toISOString().slice(0, 10),
              })
            }
          />
        </div>

        {customBillingRange && (
          <div style={{ ...RADIO_GROUP_STYLE, marginTop: "8px" }}>
            <label style={{ display: "block", marginBottom: "12px" }}>
              <div
                style={{
                  fontSize: "13px",
                  opacity: 0.8,
                  marginBottom: "6px",
                }}
              >
                Start Date
              </div>
              <input
                type="date"
                value={mergedOptions.billingPeriodStart || ""}
                disabled={billingPeriodLocked}
                onChange={(event) =>
                  update({ billingPeriodStart: event.target.value })
                }
                style={DATE_INPUT_STYLE}
              />
            </label>
            <label style={{ display: "block" }}>
              <div
                style={{
                  fontSize: "13px",
                  opacity: 0.8,
                  marginBottom: "6px",
                }}
              >
                End Date
              </div>
              <input
                type="date"
                value={mergedOptions.billingPeriodEnd || ""}
                disabled={billingPeriodLocked}
                onChange={(event) =>
                  update({ billingPeriodEnd: event.target.value })
                }
                style={DATE_INPUT_STYLE}
              />
            </label>
          </div>
        )}

        {billingPeriodLocked && (
          <p style={{ fontSize: "13px", opacity: 0.65, margin: "8px 0 0" }}>
            Billing period is locked after the invoice is sent.
          </p>
        )}
      </SectionBlock>

      <SectionBlock title="Reference">
        <CheckboxRow
          label="Show Invoice Number"
          checked={mergedOptions.showInvoiceNumber}
          onChange={() => toggle("showInvoiceNumber")}
        />
        <CheckboxRow
          label="Show Invoice Date"
          checked={mergedOptions.showInvoiceDate}
          onChange={() => toggle("showInvoiceDate")}
        />
        <CheckboxRow
          label="Show Billing Period"
          checked={mergedOptions.showBillingPeriod}
          onChange={() => toggle("showBillingPeriod")}
        />
        <CheckboxRow
          label="Show Source Estimate"
          checked={mergedOptions.showSourceEstimate}
          onChange={() => toggle("showSourceEstimate")}
        />
        <CheckboxRow
          label="Show Client Reference / PO Number"
          checked={mergedOptions.showClientReference}
          onChange={() => toggle("showClientReference")}
        />
      </SectionBlock>

      <button
        type="button"
        className="nx-btn nx-btn-gold"
        style={{ width: "100%" }}
        onClick={() => onPreview?.(mergedOptions)}
      >
        Preview Invoice
      </button>
    </CrewPilotModuleShell>
  );
}
